import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useWorkleapAiClient } from "@workleap-ai/client/react";
import { useI18nextInstance } from "@squide/i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useEngagementClient } from "../api/query.ts";
import { useEngagementFilters } from "../hooks/useEngagementFilters.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";

const EXPORT_ENGAGEMENT_SKILL_SLUG = "wov_export_engagement";
export type ExportEngagementFormat = "Pdf" | "Pptx";

const FORMAT_SKILL_SLUG_BY_FORMAT: Record<ExportEngagementFormat, string> = {
    Pdf: "wai_pdf",
    Pptx: "wai_pptx"
};

export interface UseCreateEngagementPresentationResult {
    createPresentation: (format: ExportEngagementFormat) => Promise<void>;
    isCreating: boolean;
}

export function useCreateEngagementPresentation(): UseCreateEngagementPresentationResult {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("EngagementPageActions", { i18n: i18nextInstance });

    const aiClient = useWorkleapAiClient();
    const engagementClient = useEngagementClient();
    const queryClient = useQueryClient();
    const { scope, timeFrame } = useEngagementFilters();
    const session = useSession();

    const [isCreating, setIsCreating] = useState(false);

    const scopePhrase = scope === "organization" ? (session?.organizationName ?? t("scopePhrase.organizationFallback")) : t(`scopePhrase.${scope}`);

    const createPresentation = useCallback(
        async (format: ExportEngagementFormat) => {
            setIsCreating(true);

            try {
                const data = await queryClient.fetchQuery(createQueryOptions(engagementClient.getEngagementReportExportContext.queryOptions({ input: { scope, timeFrame } })));

                aiClient.askAssistant({
                    prompt: t(`createCustomPresentationPrompt.${format}`, { scope: scopePhrase, timeFrame: t(`timeFramePhrase.${timeFrame}`) }),
                    promptFeature: EXPORT_ENGAGEMENT_SKILL_SLUG,
                    selectedSkillSlugs: [EXPORT_ENGAGEMENT_SKILL_SLUG, FORMAT_SKILL_SLUG_BY_FORMAT[format]],
                    promptContext: JSON.stringify(data),
                    disableSearch: true
                });
            } catch {
                toast.error(t("createCustomPresentationFailed"));
            } finally {
                setIsCreating(false);
            }
        },
        [aiClient, engagementClient, queryClient, scope, scopePhrase, t, timeFrame]
    );

    return { createPresentation, isCreating };
}
